/**
 * Page catalog — the single source of truth for which pages exist, their label,
 * their (existing, unchanged) route, and the minimum auth role to access them.
 *
 * `minRole` preserves today's access model: the pages that were admin-only in
 * the flat sidebar (events, audit, azure, drift, users) plus the new editor
 * (seitenzuordnung) require `admin`; everything else is `viewer`.
 *
 * NC-1270: `rooms` left that admin set. It is reference data every tutor needs
 * (a run points at a room), and the API always allowed `viewer` to read it —
 * only the mutations are admin-only, which RoomsPage now enforces per action.
 *
 * Plain data only (no JSX) so selectors/store stay trivially unit-testable.
 * Icons are resolved separately in `navIcons.tsx` keyed by the same PageId.
 */
import { PageId, PageCatalogEntry } from './navTypes';

export const PAGE_CATALOG: Record<PageId, PageCatalogEntry> = {
  dashboard: { label: 'Dashboard', route: '/dashboard', minRole: 'viewer' },
  runs: { label: 'Schulungsläufe', route: '/runs', minRole: 'viewer' },
  // NC-1269: read-only year/month calendar over the same runs — viewer, like
  // the list it is a second view onto.
  runscalendar: { label: 'Schulungskalender', route: '/runs/kalender', minRole: 'viewer' },
  // NC-1478: /api/participants verlangt seit dem withEndpoint-Composer Operator + Tenant-Filter.
  participants: { label: 'Teilnehmer', route: '/participants', minRole: 'operator' },
  rooms: { label: 'Schulungsräume', route: '/rooms', minRole: 'viewer' },
  costcenters: { label: 'Kostenstellen', route: '/cost-centers', minRole: 'admin' },
  finops: { label: 'FinOps / Kosten', route: '/finops', minRole: 'viewer' },
  workloads: { label: 'Workloads', route: '/workloads', minRole: 'viewer' },
  installers: { label: 'Installers', route: '/installers', minRole: 'viewer' },
  images: { label: 'Image-Builds', route: '/images', minRole: 'viewer' },
  // NC-1240: entry point to the guided image wizard (NC-972, live). Operator+
  // only — the route already guards it, and the flag NC_972_WIZARD_ENABLED is on.
  newimage: { label: 'Neues Image', route: '/images/new', minRole: 'operator' },
  // Image Builder 2.1 Phase C (#1712): app catalog (tenant-wide) + registration wizard.
  apps: { label: 'App-Katalog', route: '/apps', minRole: 'viewer' },
  appsregister: { label: 'App registrieren', route: '/apps/register', minRole: 'operator' },
  buildlogs: { label: 'Build-Logs', route: '/builds', minRole: 'viewer' },
  status: { label: 'System Status', route: '/status', minRole: 'viewer' },
  logs: { label: 'Logs', route: '/logs', minRole: 'viewer' },
  azure: { label: 'Azure Resources', route: '/azure-resources', minRole: 'admin' },
  drift: { label: 'Drift-Check', route: '/admin/drift', minRole: 'admin' },
  users: { label: 'Tutoren', route: '/users', minRole: 'admin' },
  audit: { label: 'Audit-Log', route: '/audit', minRole: 'admin' },
  events: { label: 'Feedback & Fehler', route: '/events', minRole: 'admin' },
  changelog: { label: 'Neuigkeiten', route: '/neuigkeiten', minRole: 'viewer' },
  wiki: { label: 'Wiki', route: '/wiki', minRole: 'viewer' },
  // NC-1103 (Feat 2.0 P5): step-by-step guides (Schulung/Workload anlegen).
  hilfe: { label: 'Hilfe', route: '/hilfe', minRole: 'viewer' },
  settings: { label: 'Einstellungen', route: '/settings', minRole: 'viewer' },
  seitenzuordnung: { label: 'Seitenzuordnung', route: '/admin/navigation', minRole: 'admin' },
};

/** Stable, declaration-ordered list of every known page id. */
export const ALL_PAGE_IDS: PageId[] = Object.keys(PAGE_CATALOG);
